import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBugDetails } from "../../services/BugService";
import "./BugDetail.css";
import Header from "../../components/Header/Header";
import {
  FiX,
  FiPaperclip,
  FiClock,
  FiUser,
  FiCheckCircle,
  FiAlertTriangle
} from "react-icons/fi";

const BugDetail = ({ bugId: propBugId, onClose: propOnClose }) => {
  const params = useParams();
  const navigate = useNavigate();

  const id = propBugId ?? params.bugId;

  const [bug, setBug] = useState(null);
  const [logs, setLogs] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);

  const dialogRef = useRef(null);

  const formatDateTimeTR = (val) => {
    if (!val) return "";
    const raw = String(val).trim();
    let d = null;

    if (typeof val === "number") {
      d = new Date(val);
    } else {
      const isoLike = raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;
      d = new Date(isoLike);
      if (Number.isNaN(d.getTime())) d = new Date(raw);
    }

    if (!d || Number.isNaN(d.getTime())) return raw;

    const datePart = d.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });

    const timePart = d.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit"
    });

    return `${datePart} • ${timePart}`;
  };

  const asTs = (val) => {
    if (!val) return 0;
    const raw = String(val).trim();
    const isoLike = raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;

    const d = new Date(isoLike);
    if (!Number.isNaN(d.getTime())) return d.getTime();

    const d2 = new Date(raw);
    if (!Number.isNaN(d2.getTime())) return d2.getTime();

    return 0;
  };

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.focus();
    }
  }, []);

  const fetchBugDetails = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getBugDetails(id);
      setBug(data.bug);
      setLogs(Array.isArray(data.logs) ? data.logs : []);
      setAttachments(Array.isArray(data.attachments) ? data.attachments : []);
    } catch (error) {
      console.error("Bug detayları alınamadı:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBugDetails();
  }, [fetchBugDetails]);

  useEffect(() => {
    const onFocus = () => {
      fetchBugDetails();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchBugDetails();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchBugDetails]);

  const handleClose = useCallback(() => {
    if (typeof propOnClose === "function") {
      propOnClose();
    } else {
      navigate(-1);
    }
  }, [propOnClose, navigate]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose]);

  const headerButtons = [];

  const statusInfo = (status) => {
    if (status === "on") {
      return {
        label: "Tamamlandı",
        className: "completed",
        icon: <FiCheckCircle size={14} aria-hidden="true" />
      };
    }
    return {
      label: "Devam Ediyor",
      className: "incomplete",
      icon: <FiAlertTriangle size={14} aria-hidden="true" />
    };
  };

  const fileNameFromPath = (p) => {
    try {
      if (!p) return "Dosya";
      const url = new URL(p, window.location.origin);
      const parts = url.pathname.split("/");
      const last = parts[parts.length - 1] || "Dosya";
      return decodeURIComponent(last);
    } catch {
      const parts = String(p).split("/");
      return decodeURIComponent(parts[parts.length - 1] || "Dosya");
    }
  };

  const displayedLogs = useMemo(() => {
    const base = Array.isArray(logs) ? logs : [];
    if (!bug) return base;

    const createdRaw = bug.created_at || "";
    const updatedRaw = bug.updated_at || "";

    const createdTs = asTs(createdRaw);
    const updatedTs = asTs(updatedRaw);

    const hasUpdatedMeaningfully =
      !!updatedTs &&
      !!createdTs &&
      updatedTs > createdTs + 30000;

    const existsAdded = base.some((l) => {
      const act = String(l?.action || "").toLowerCase();
      return act === "added" || act === "created";
    });

    const existsUpdated = base.some((l) => {
      const act = String(l?.action || "").toLowerCase();
      return act === "updated" || act === "edited";
    });

    const whoAdded =
      bug.creator_name ||
      bug.creator_email ||
      bug.created_by_name ||
      bug.created_by ||
      "Sistem";

    const whoUpdated =
      bug.updated_by_name ||
      bug.updater_name ||
      bug.updated_by ||
      bug.creator_name ||
      bug.creator_email ||
      "Sistem";

    const mapped = base.map((l) => {
      const t = asTs(l?.created_at) || 0;
      return {
        ...l,
        __ts: t || Date.now(),
        created_at_formatted: l?.created_at ? formatDateTimeTR(l.created_at) : ""
      };
    });

    const extras = [];

    if (createdRaw && !existsAdded) {
      extras.push({
        action: "added",
        user_name: whoAdded,
        created_at: createdRaw,
        created_at_formatted: formatDateTimeTR(createdRaw),
        __ts: createdTs || Date.now(),
        __synthetic: true
      });
    }

    if (updatedRaw && hasUpdatedMeaningfully && !existsUpdated) {
      extras.push({
        action: "updated",
        user_name: whoUpdated,
        created_at: updatedRaw,
        created_at_formatted: formatDateTimeTR(updatedRaw),
        __ts: updatedTs || Date.now(),
        __synthetic: true
      });
    }

    const all = [...mapped, ...extras];

    all.sort((a, b) => {
      const at = a?.__ts || 0;
      const bt = b?.__ts || 0;
      return bt - at;
    });

    return all;
  }, [logs, bug]);

  const detailStatus = bug ? statusInfo(bug.status) : null;

  return (
    <>
      <Header buttons={headerButtons} />
      <div
        className="bugdetail-overlay"
        onMouseDown={(e) => {
          if (e.target.classList.contains("bugdetail-overlay")) handleClose();
        }}
      >
        <div
          ref={dialogRef}
          className="bugdetail-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bugdetail-title"
          tabIndex={-1}
        >
          <button
            type="button"
            className="bugdetail-close"
            aria-label="Kapat"
            onClick={handleClose}
          >
            <FiX size={18} />
            <span>Kapat</span>
          </button>

          <div className="bug-detail-container" aria-busy={loading ? "true" : "false"}>
            {loading && (
              <div className="skeleton">
                <div className="sk-title" />
                <div className="sk-row" />
                <div className="sk-row short" />
                <div className="sk-block" />
                <div className="sk-block" />
              </div>
            )}

            {!loading && bug && (
              <>
                <header className="bug-header">
                  <div className="bug-header-copy">
                    <span className="bug-header-kicker">Buglog Detay</span>
                    <h1 id="bugdetail-title" title={bug.title || ""}>
                      {bug.title}
                    </h1>
                    <p className="bug-header-subtitle">
                      İş içeriği, ekler ve işlem geçmişi tek ekranda toplanır.
                    </p>
                  </div>

                  <div className="bug-header-side">
                    <span
                      className={`status-badge ${detailStatus?.className || ""}`}
                      aria-live="polite"
                    >
                      {detailStatus?.icon}
                      <span className="status-text">{detailStatus?.label}</span>
                    </span>

                    <div className="bug-header-stats">
                      <div className="bug-header-stat">
                        <strong>{attachments.length}</strong>
                        <span>Ek</span>
                      </div>
                      <div className="bug-header-stat">
                        <strong>{displayedLogs.length}</strong>
                        <span>Hareket</span>
                      </div>
                    </div>
                  </div>
                </header>

                <section className="bug-meta">
                  <div className="bug-meta-item is-pill">
                    <FiUser aria-hidden="true" />
                    <div>
                      <div className="meta-label">Oluşturan</div>
                      <div className="meta-value">{bug.creator_email}</div>
                    </div>
                  </div>

                  {bug.created_at && (
                    <div className="bug-meta-item is-pill is-date">
                      <FiClock aria-hidden="true" />
                      <div>
                        <div className="meta-label">Oluşturma</div>
                        <div className="meta-value">{formatDateTimeTR(bug.created_at)}</div>
                      </div>
                    </div>
                  )}

                  {bug.updated_at && (
                    <div className="bug-meta-item is-pill is-date">
                      <FiClock aria-hidden="true" />
                      <div>
                        <div className="meta-label">Güncelleme</div>
                        <div className="meta-value">{formatDateTimeTR(bug.updated_at)}</div>
                      </div>
                    </div>
                  )}
                </section>

                <section className="bug-panel bug-info">
                  <div className="bug-panel-head">
                    <h2>Açıklama</h2>
                    <span className="bug-panel-chip">
                      {bug.description ? "Detay mevcut" : "Detay yok"}
                    </span>
                  </div>
                  <div
                    className="bug-description"
                    dangerouslySetInnerHTML={{ __html: bug.description || "<p>Bu iş için açıklama eklenmemiş.</p>" }}
                  />
                </section>

                <section className="bug-panel bug-attachments">
                  <div className="bug-panel-head">
                    <h2>Ekler</h2>
                    <span className="bug-panel-chip">{attachments.length} dosya</span>
                  </div>
                  {attachments.length > 0 ? (
                    <ul className="attachment-list">
                      {attachments.map((attachment) => (
                        <li key={attachment.id} className="attachment-item">
                          <FiPaperclip className="att-icon" aria-hidden="true" />
                          <a
                            href={attachment.file_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={attachment.file_path}
                          >
                            {fileNameFromPath(attachment.file_path)}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="empty">Ek bulunmuyor.</p>
                  )}
                </section>

                <section className="bug-panel bug-logs">
                  <div className="bug-panel-head">
                    <h2>İşlem Geçmişi</h2>
                    <span className="bug-panel-chip">{displayedLogs.length} kayıt</span>
                  </div>
                  {displayedLogs.length > 0 ? (
                    <div className="logs-timeline">
                      {displayedLogs.map((log, index) => (
                        <div key={`${index}-${log.__synthetic ? "s" : "r"}`} className="log-item">
                          <div className="log-dot" aria-hidden="true" />
                          <div className="log-content">
                            <p className="log-title">
                              {String(log.action || "").toLowerCase() === "updated" ||
                                String(log.action || "").toLowerCase() === "edited"
                                ? "İş Güncellendi"
                                : String(log.action || "").toLowerCase() === "added" ||
                                  String(log.action || "").toLowerCase() === "created"
                                  ? "İş Eklendi"
                                  : "Bilinmeyen hareket"}
                            </p>
                            <div className="log-meta">
                              <span className="log-user">{log.user_name}</span>
                              <span className="log-sep">•</span>
                              <span className="log-timestamp">
                                {log.created_at_formatted || formatDateTimeTR(log.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="empty">Herhangi bir işlem kaydı bulunmuyor.</p>
                  )}
                </section>
              </>
            )}

            {!loading && !bug && <div className="empty error">Kayıt bulunamadı.</div>}
          </div>
        </div>
      </div>
    </>
  );
};

export default BugDetail;
